<template>
  <DialogPopup
    ref="dialog"
    v-show="isOpened"
    :title="$t('playerSetup.dialogTitle')"
    :prevButtonTitle="$t('playerSetup.navCancel')"
    :nextButtonTitle="$t('playerSetup.navNext')"
    :isOpened="isOpened"
    :progress="progress"
    :progressScenarioId="progressScenarioId"
    v-slot:default="slotProps"
    v-on:DialogClose="handleDialogClose"
    v-on:PageChange="handlePageChange">

    <PlayerSetupIcon v-if="isIconVisible" :deviceType="deviceType" :licenses="licenses"></PlayerSetupIcon>

    <DialogPopupPage
      :default="isPlanSummaryVisible"
      :navigationNext="$t('playerSetup.navConfirm')"
      :navigationPrev="$t('playerSetup.navCancel')"
      :onNext="handlePlanConfirmation"
      :onPrev="handleBackButton(steps.CLOSE)"
      :pageId="steps.STEP_PLAN_SUMMARY"
      :showPrevButton="true"
      :showNextButton="true"
      v-show="isPlanSummaryVisible && slotProps?.activePageId === steps.STEP_PLAN_SUMMARY">
        <player-setup-plan-summary
        ref="pagePlanSummary"
        :enableSummary="isPlanSummaryVisible"
        :nextPage="steps.STEP_SERIAL"
        :isActive="(!slotProps?.activePageId || slotProps?.activePageId === steps.STEP_PLAN_SUMMARY) && isOpened"
        v-on:Confirm="handlePlanConfirmation"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :onNext="handlers.submitSerialForm"
      :pageId="steps.STEP_SERIAL"
      :default="!isPlanSummaryVisible"
      :showNextButton="true"
      v-show="slotProps?.activePageId === this.steps.STEP_SERIAL && !isRestart">
      <player-setup-serial
        ref="pageSerial"
        :serial="serialNumber"
        :externalError="validationErrors.serialForm"
        :isActive="(!slotProps?.activePageId || slotProps?.activePageId === steps.STEP_SERIAL) && isOpened"
        v-on:SubmitForm="handlers.submitSerialForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :onNext="handlers.submitPinForm"
      :pageId="steps.STEP_PIN"
      :showNextButton="true"
      v-show="slotProps?.activePageId === steps.STEP_PIN">
      <player-setup-pin
        ref="pagePin"
        :serial="serial"
        :nextPage="steps.STEP_NAME"
        :isActive="slotProps?.activePageId === steps.STEP_PIN && isOpened"
        v-on:SubmitForm="handlers.submitPinForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      progressScenarioId="SINGLE_DISPLAY"
      :onNext="handlers.submitNameForm"
      :pageId="steps.STEP_NAME"
      :showNextButton="true"
      v-show="slotProps?.activePageId === steps.STEP_NAME">
      <player-setup-name
        ref="pageName"
        :serial="serial"
        :timezone="timezone"
        :name="name"
        :externalError="validationErrors.nameForm"
        :nextPage="nextPage(steps.STEP_NAME)"
        :isActive="slotProps?.activePageId === steps.STEP_NAME && isOpened"
        v-on:SubmitForm="handlers.submitNameForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :showPrevButton="true"
      :showNextButton="true"
      :navigationPrev="$t('playerSetup.navPrev')"
      :onPrev="handleBackButton(steps.STEP_APPLICATION)"
      :onNext="handlers.submitApplicationForm"
      :pageId="steps.STEP_APPLICATION"
      v-show="slotProps?.activePageId === steps.STEP_APPLICATION">
      <player-setup-application
        ref="pageApplication"
        application="single"
        :isLEDAvailable="isLEDAvailable"
        :licenses="licenses"
        :isDiscovery="isDiscovery"
        :isPremium="isPremiumPlan"
        :nextPage="nextPage(steps.STEP_APPLICATION)"
        :isActive="slotProps?.activePageId === steps.STEP_APPLICATION && isOpened"
        v-on:SubmitForm="handlers.submitApplicationForm"
        v-on:SetApplication="handleSetApplication"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :showPrevButton="true"
      :showNextButton="true"
      :navigationPrev="$t('playerSetup.navPrev')"
      :navigationNext="isLastStep(steps.STEP_RESOLUTION) ? $t('playerSetup.navFinish') : $t('playerSetup.navNext')"
      :onPrev="handleBackButton(steps.STEP_RESOLUTION)"
      :onNext="handlers.submitDisplayForm"
      :pageId="steps.STEP_RESOLUTION"
      v-show="slotProps?.activePageId === steps.STEP_RESOLUTION">
      <player-setup-display
        ref="pageDisplay"
        :serial="serial"
        :applicationType="applicationType"
        :hasMultiOutput="hasMultiOutput()"
        :outputs="outputs"
        :resolutions="resolutions"
        :resolution="resolution"
        :orientation="orientation"
        :externalError="validationErrors.displayForm"
        :nextPage="nextPage(steps.STEP_RESOLUTION)"
        :isActive="slotProps?.activePageId === steps.STEP_RESOLUTION && isOpened"
        v-on:SubmitForm="handlers.submitDisplayForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :showPrevButton="true"
      :showNextButton="true"
      :navigationPrev="$t('playerSetup.navPrev')"
      :onPrev="handleBackButton(steps.STEP_ARRANGEMENT)"
      :onNext="handlers.submitArrangementForm"
      :pageId="steps.STEP_ARRANGEMENT"
      v-show="slotProps?.activePageId === steps.STEP_ARRANGEMENT">
      <player-setup-arrangement
        ref="pageArrangement"
        :orientation="orientation"
        :outputs="selectedOutputCount"
        :nextPage="nextPage(steps.STEP_ARRANGEMENT)"
        :isActive="slotProps?.activePageId === steps.STEP_ARRANGEMENT && isOpened"
        v-on:SubmitForm="handlers.submitArrangementForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :navigationNext="$t('playerSetup.navFinish')"
      :showPrevButton="true"
      :showNextButton="true"
      :navigationPrev="$t('playerSetup.navPrev')"
      :onPrev="handleBackButton(steps.STEP_ADJUSTMENTS)"
      :onNext="handlers.submitAdjustmentsForm"
      :pageId="steps.STEP_ADJUSTMENTS"
      v-show="slotProps?.activePageId === steps.STEP_ADJUSTMENTS && applicationType === PLAYER_APPLICATIONS.APP_LED">
      <player-setup-led
        ref="pageAdjustmentsLed"
        :arrangementName="arrangement"
        :displayOrientation="orientation"
        :selectedResolution="resolution"
        :applicationType="applicationType"
        :nextPage="steps.CLOSE"
        :externalError="validationErrors.adjustmentsForm"
        v-on:SubmitForm="handlers.submitAdjustmentsForm"
      />
    </DialogPopupPage>

    <DialogPopupPage
      :navigationNext="$t('playerSetup.navFinish')"
      :showPrevButton="true"
      :showNextButton="true"
      :navigationPrev="$t('playerSetup.navPrev')"
      :onPrev="handleBackButton(steps.STEP_ADJUSTMENTS)"
      :onNext="handlers.submitAdjustmentsForm"
      :pageId="steps.STEP_ADJUSTMENTS"
      v-show="slotProps?.activePageId === steps.STEP_ADJUSTMENTS && applicationType === PLAYER_APPLICATIONS.APP_VIDEOWALL">
      <player-setup-videowall
        ref="pageAdjustmentsVideowall"
        :arrangementName="arrangement"
        :displayOrientation="orientation"
        :selectedResolution="resolution"
        :applicationType="applicationType"
        :canvasSize="canvasSize"
        :nextPage="steps.CLOSE"
        :isActive="slotProps?.activePageId === steps.STEP_ADJUSTMENTS && isOpened"
        :externalError="validationErrors.adjustmentsForm"
        v-on:applyAdjustments="applyAdjustments"
        v-on:SubmitForm="handlers.submitAdjustmentsForm"
      />
    </DialogPopupPage>
  </DialogPopup>
</template>

<script src="./player-setup.js"></script>
